import type { FeatureCard as FeatureCardData } from '../sections/sections';
import './FeatureCard.css';

interface FeatureCardProps {
  card: FeatureCardData;
  isVisible: boolean;
}

function IconBilling() {
  return (
    <svg
      className="feature-card__stroke-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}

function IconCrm() {
  return (
    <svg
      className="feature-card__stroke-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      <circle cx="17.5" cy="6.5" r="2" />
      <path d="M17.5 5v3M16 6.5h3" />
    </svg>
  );
}

function IconAnalytics() {
  return (
    <svg
      className="feature-card__stroke-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function stackedIconForLabel(label: string) {
  const l = label.toLowerCase();
  if (l.includes('bill')) return <IconBilling />;
  if (l.includes('crm')) return <IconCrm />;
  if (l.includes('analytics')) return <IconAnalytics />;
  return null;
}

/** Three sources converging into one — the "connected" footer state (section 1) */
function FooterLinkedIcon() {
  return (
    <svg
      className="feature-card__footer-linked-svg"
      width="46"
      height="22"
      viewBox="0 0 46 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M7 6 23 17M23 6 23 17M39 6 23 17"
        stroke="#00d9a6"
        strokeOpacity="0.5"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
      <circle cx="7" cy="6" r="2.6" fill="#00d9a6" />
      <circle cx="23" cy="6" r="2.6" fill="#00d9a6" />
      <circle cx="39" cy="6" r="2.6" fill="#00d9a6" />
      <circle cx="23" cy="17" r="3" fill="#00d9a6" />
    </svg>
  );
}

/** Red alert badge (Churn / Expansion) */
function IntelAlertIcon() {
  return (
    <svg
      className="feature-card__intel-alert-svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle cx="10" cy="10" r="7.25" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M10 6.25V11M10 13.25v.01"
        stroke="currentColor"
        strokeWidth="1.45"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Curved trend + node + vertical guide (intelligence card only) */
function IntelligenceDecorGraphic() {
  return (
    <svg
      className="feature-card__intel-deco-svg"
      viewBox="0 0 64 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M 18 52 Q 34 88 28 132 T 22 198"
        stroke="rgb(255 255 255 / 22%)"
        strokeWidth="1.15"
        strokeLinecap="round"
      />
      <circle cx="22" cy="198" r="4" fill="rgb(255 255 255 / 42%)" />
      <line
        x1="22"
        y1="198"
        x2="22"
        y2="228"
        stroke="rgb(255 255 255 / 18%)"
        strokeWidth="1"
        strokeDasharray="3 5"
      />
      {Array.from({ length: 12 }).map((_, i) => (
        <circle
          key={`g-${i}`}
          cx={12 + (i % 2) * 6}
          cy={14 + i * 16}
          r={1}
          fill="rgb(45 212 191 / 16%)"
        />
      ))}
    </svg>
  );
}

/** Faint path / dots behind module list (reference layout) */
function ModulesDecorGraphic() {
  return (
    <svg
      className="feature-card__modules-deco-svg"
      viewBox="0 0 72 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      {Array.from({ length: 14 }).map((_, i) => (
        <circle
          key={`d-${i}`}
          cx={10}
          cy={12 + i * 14}
          r={1.1}
          fill="rgb(255 255 255 / 14%)"
        />
      ))}
      <path
        d="M 16 48 C 32 72 38 108 26 156"
        stroke="rgb(255 255 255 / 16%)"
        strokeWidth={1}
        strokeLinecap="round"
      />
      <circle cx={26} cy={156} r={3.5} fill="rgb(255 255 255 / 28%)" />
      <path
        d="M 0 180 H 72"
        stroke="rgb(255 255 255 / 6%)"
        strokeWidth={1}
        strokeDasharray="2 5"
      />
    </svg>
  );
}

export function FeatureCard({ card, isVisible }: FeatureCardProps) {
  const isRevenueAppChrome = Boolean(card.overview && card.stats && card.stats.length > 0);
  const isUnifiedAppChrome = Boolean(
    card.subCards && card.subCards.length > 0 && card.brandCard,
  );
  const isIntelligenceAppChrome = Boolean(card.dataSections && card.dataSections.length > 0);
  const isModulesAppChrome = Boolean(card.modules && card.modules.length > 0);
  const platformModulesSplit = (() => {
    const m = card.modules;
    if (!m?.length) return null;
    const mid = Math.ceil(m.length / 2);
    return { first: m.slice(0, mid), second: m.slice(mid) };
  })();

  const rootClass = [
    'feature-card',
    isVisible ? 'feature-card--visible' : '',
    card.stackedItems ? 'feature-card--clip-stack' : '',
    isRevenueAppChrome ? 'feature-card--revenue-shell' : '',
    isUnifiedAppChrome ? 'feature-card--unified-shell' : '',
    isIntelligenceAppChrome ? 'feature-card--intelligence-shell' : '',
    isModulesAppChrome ? 'feature-card--modules-shell' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      {card.description && (
        <p
          className="feature-card__description"
          dangerouslySetInnerHTML={{ __html: card.description }}
        />
      )}

      {card.items && card.items.length > 0 && (
        <div
          className={`feature-card__stacked ${card.stackedItems ? 'feature-card__stacked--floating' : ''} ${card.connected ? 'feature-card__stacked--connected' : ''}`}
        >
          {card.items.map((item, index) => (
            <div key={`${item.label}-${index}`} className="feature-card__stacked-card">
              <div className="feature-card__stacked-top">
                <span className="feature-card__stacked-label">{item.label}</span>
                <span className="feature-card__stacked-icon-wrap" aria-hidden>
                  {stackedIconForLabel(item.label)}
                </span>
              </div>
              <div className="feature-card__stacked-bottom">
                <span className="feature-card__stacked-value">{item.value ?? '—'}</span>
              </div>
            </div>
          ))}

          {card.connected && (
            <div className="feature-card__connector" aria-hidden>
              <span className="feature-card__connector-rail" />
              {card.items.map((item, index) => (
                <span key={`node-${item.label}-${index}`} className="feature-card__connector-node" />
              ))}
            </div>
          )}
        </div>
      )}

      {isRevenueAppChrome && card.overview && (
        <div className="feature-card__app-window">
          <div className="feature-card__app-window-body">
            <div className="feature-card__app-sidebar" aria-hidden>
              <div className="feature-card__app-sidebar-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="feature-card__app-main">
              <div className="feature-card__app-main-fade">
                <div className="feature-card__app-panel feature-card__app-panel--overview">
                  {card.overview.title && (
                    <span className="feature-card__app-panel-title">{card.overview.title}</span>
                  )}
                  <span className="feature-card__app-panel-amount">{card.overview.amount}</span>
                  <p className="feature-card__app-panel-subtitle">{card.overview.subtitle}</p>
                </div>
                {(card.stats ?? []).map((stat) => (
                  <div
                    key={stat.label}
                    className="feature-card__app-panel feature-card__app-panel--stat"
                  >
                    <span className="feature-card__app-panel-stat-label">{stat.label}</span>
                    <span className="feature-card__app-panel-stat-value">{stat.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {card.overview && !isRevenueAppChrome && (
        <div className="feature-card__overview">
          <div className="feature-card__window-chrome">
            <div className="feature-card__overview-dots" aria-hidden>
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="feature-card__overview-body">
            <div className="feature-card__overview-sidebar" aria-hidden />
            <div className="feature-card__overview-inner">
              {card.overview.title && (
                <p className="feature-card__overview-heading">{card.overview.title}</p>
              )}
              <span className="feature-card__overview-amount">{card.overview.amount}</span>
              <p className="feature-card__overview-subtitle">{card.overview.subtitle}</p>
            </div>
          </div>
        </div>
      )}

      {card.stats && card.stats.length > 0 && !isRevenueAppChrome && (
        <div className="feature-card__stats">
          {card.stats.map((stat) => (
            <div key={stat.label} className="feature-card__stat">
              <span className="feature-card__stat-label">{stat.label}</span>
              <span className="feature-card__stat-value">{stat.value}</span>
            </div>
          ))}
        </div>
      )}

      {isUnifiedAppChrome && card.subCards && (
        <div className="feature-card__app-window feature-card__app-window--unified">
          <div className="feature-card__app-window-body">
            <div className="feature-card__app-sidebar" aria-hidden>
              <div className="feature-card__app-sidebar-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="feature-card__app-main">
              <div className="feature-card__app-main-fade feature-card__app-main-fade--unified">
                <div className="feature-card__unified-grid">
                  {card.subCards.map((sub, index) => (
                    <div key={`${sub.title}-${index}`} className="feature-card__unified-panel">
                      <p className="feature-card__unified-panel-title">{sub.title}</p>
                      {sub.rows.map((row, rowIdx) => (
                        <div key={`${row.label}-${rowIdx}`} className="feature-card__unified-panel-row">
                          <span className="feature-card__unified-panel-label">{row.label}</span>
                          <span className="feature-card__unified-panel-value">
                            {row.value?.trim() ? row.value : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="feature-card__unified-brand">
                  <span className="feature-card__unified-brand-name">Revvly</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {card.subCards && card.subCards.length > 0 && !isUnifiedAppChrome && (
        <div className="feature-card__sub-cards">
          {card.subCards.map((sub, index) => (
            <div key={`${sub.title}-${index}`} className="feature-card__sub">
              <p className="feature-card__sub-title">{sub.title}</p>
              {sub.rows.map((row, rowIdx) => (
                <div key={`${row.label}-${rowIdx}`} className="feature-card__sub-row">
                  <span className="feature-card__sub-label">{row.label}</span>
                  <span className="feature-card__sub-value">{row.value || '—'}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {card.brandCard && !isUnifiedAppChrome && (
        <div className="feature-card__brand">
          <div className="feature-card__brand-name">Revvly</div>
        </div>
      )}

      {isIntelligenceAppChrome && card.dataSections && (
        <div className="feature-card__app-window feature-card__app-window--intelligence">
          <div className="feature-card__app-window-body">
            <div className="feature-card__app-sidebar" aria-hidden>
              <div className="feature-card__app-sidebar-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="feature-card__app-main">
              <div className="feature-card__app-main-fade feature-card__app-main-fade--intelligence">
                <div className="feature-card__intel-app">
                  <IntelligenceDecorGraphic />
                  <div className="feature-card__intel-stack">
                    {card.dataSections.map((data, index) => (
                      <div key={`${data.title}-${index}`} className="feature-card__intel-panel">
                        <div className="feature-card__intel-panel-header">
                          <span className="feature-card__intel-panel-title">{data.title}</span>
                          <span className="feature-card__intel-panel-indicators" aria-hidden>
                            {(data.indicator === 'danger' || data.indicator === 'warning') && (
                              <IntelAlertIcon />
                            )}
                          </span>
                        </div>
                        {data.rows && data.rows.length > 0 && (
                          <div className="feature-card__intel-panel-body">
                            {data.rows.map((row, rowIdx) => (
                              <div key={`${row.label}-${rowIdx}`} className="feature-card__intel-panel-row">
                                <span className="feature-card__intel-panel-label">{row.label}</span>
                                <span className="feature-card__intel-panel-value">{row.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {data.miniChart && (
                          <div className="feature-card__intel-chart-wrap">
                            {data.chartCaption && (
                              <p className="feature-card__intel-chart-label">{data.chartCaption}</p>
                            )}
                            <div className="feature-card__intel-chart" aria-hidden>
                              {[14, 20, 26, 34].map((h, i) => (
                                <div
                                  key={i}
                                  className={`feature-card__intel-bar feature-card__intel-bar--blue ${i === 3 ? 'feature-card__intel-bar--blue-strong' : ''}`}
                                  style={{ height: `${h}px` }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {card.dataSections && card.dataSections.length > 0 && !isIntelligenceAppChrome && (
        <div className="feature-card__sections">
          {card.dataSections.map((data, index) => (
            <div key={`${data.title}-${index}`} className="feature-card__section">
              <div className="feature-card__section-header">
                <span className="feature-card__section-title">{data.title}</span>
                {data.indicator === 'danger' && (
                  <span className="feature-card__indicator feature-card__indicator--danger" />
                )}
                {data.indicator === 'warning' && (
                  <span className="feature-card__indicator feature-card__indicator--warning" />
                )}
              </div>
              {data.rows && data.rows.length > 0 && (
                <>
                  {data.rows.map((row, rowIdx) => (
                    <div key={`${row.label}-${rowIdx}`} className="feature-card__section-row">
                      <span className="feature-card__section-label">{row.label}</span>
                      <span className="feature-card__section-value">{row.value}</span>
                    </div>
                  ))}
                </>
              )}
              {data.miniChart && (
                <div className="feature-card__chart" aria-hidden>
                  {[14, 22, 16, 28, 18].map((h, i) => (
                    <div
                      key={i}
                      className={`feature-card__bar ${i === 3 ? 'feature-card__bar--accent' : ''}`}
                      style={{ height: `${h}px` }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isModulesAppChrome && platformModulesSplit && (
        <div className="feature-card__app-window feature-card__app-window--modules">
          <div className="feature-card__app-window-body">
            <div className="feature-card__app-sidebar" aria-hidden>
              <div className="feature-card__app-sidebar-dots">
                <span />
                <span />
                <span />
              </div>
            </div>
            <div className="feature-card__app-main">
              <div className="feature-card__app-main-fade feature-card__app-main-fade--modules">
                <div className="feature-card__modules-app">
                  <ModulesDecorGraphic />
                  <div className="feature-card__modules-panels">
                    <div className="feature-card__module-panel">
                      <div className="feature-card__module-panel-body">
                        {platformModulesSplit.first.map((label, index) => (
                          <div key={`${label}-a-${index}`} className="feature-card__module-row">
                            <span className="feature-card__module-row-label">{label}</span>
                            <span className="feature-card__module-row-accent" aria-hidden />
                          </div>
                        ))}
                      </div>
                    </div>
                    {platformModulesSplit.second.length > 0 && (
                      <div className="feature-card__module-panel feature-card__module-panel--secondary">
                        <div className="feature-card__module-panel-body">
                          {platformModulesSplit.second.map((label, index) => (
                            <div key={`${label}-b-${index}`} className="feature-card__module-row">
                              <span className="feature-card__module-row-label">{label}</span>
                              <span className="feature-card__module-row-accent" aria-hidden />
                            </div>
                          ))}
                        </div>
                        <div className="feature-card__modules-mini-chart" aria-hidden>
                          {[12, 20, 15, 26, 17].map((h, i) => (
                            <div
                              key={i}
                              className={`feature-card__modules-mini-bar ${i === 3 ? 'feature-card__modules-mini-bar--glow' : ''}`}
                              style={{ height: `${h}px` }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {card.modules && card.modules.length > 0 && !isModulesAppChrome && (
        <div className="feature-card__modules">
          <div className="feature-card__modules-grid">
            {card.modules.map((label, index) => (
              <div key={`${label}-${index}`} className="feature-card__module-tag">
                {label}
              </div>
            ))}
          </div>
        </div>
      )}

      {card.footer && (
        <div
          className={
            card.stackedItems
              ? `feature-card__footer-window ${card.connected ? 'feature-card__footer-window--connected' : ''}`
              : 'feature-card__footer'
          }
        >
          {card.stackedItems && (
            <>
              <div className="feature-card__window-chrome feature-card__window-chrome--footer">
                <div className="feature-card__overview-dots" aria-hidden>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <div className="feature-card__footer-window-body">
                <div className="feature-card__footer-window-sidebar" aria-hidden />
                <div className="feature-card__footer-window-main">
                  {card.connected ? (
                    <FooterLinkedIcon />
                  ) : (
                    <div className="feature-card__footer-grid-icon" aria-hidden>
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                  <p className="feature-card__footer-message">
                    {card.footer.text}
                    {card.footer.highlight && (
                      <>
                        {' '}
                        <span className="feature-card__footer-highlight">{card.footer.highlight}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </>
          )}
          {!card.stackedItems && (
            <p className="feature-card__footer-plain">
              {card.footer.text}
              {card.footer.highlight && (
                <>
                  {' '}
                  <span className="feature-card__footer-highlight">{card.footer.highlight}</span>
                </>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
